import {inject, Injectable} from '@angular/core';
import {ThirdPartyLicenseManagementDataService} from './third-party-license-management-data.service';
import {Observable, of, retry, throwError, timer} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {mapPage, Page} from '@uofu-uss/angular-util';

export type ThirdPartyLicenseManagementAction = 'ACTIVATE' | 'DEACTIVATE';

export enum ThirdPartyLicenseManagementStatus {
  Created = 'CREATED',
  Synchronized = 'SYNCHRONIZED',
  Error = 'ERROR',
}

export interface ThirdPartyLicenseManagementAnswer {
  question: string;
  answer: ReadonlyArray<string>;
}

export interface ThirdPartyLicenseManagement {
  id?: number;
  effectiveSequence?: number;
  effectiveDate?: string;
  status?: ThirdPartyLicenseManagementStatus;
  emplid: string;
  thirdPartyLicenseOfferingId: number;
  action: ThirdPartyLicenseManagementAction;
  answers: ReadonlyArray<ThirdPartyLicenseManagementAnswer>;
}

@Injectable({
  providedIn: 'root',
})
export class ThirdPartyLicenseManagementService {

  private readonly dataService = inject(ThirdPartyLicenseManagementDataService);

  createTplm(tplm: ThirdPartyLicenseManagement): Observable<ThirdPartyLicenseManagement> {
    return this.dataService.createTplm(tplm);
  }

  fetchTplm(id: number): Observable<ThirdPartyLicenseManagement> {
    return this.dataService.fetchTplm(id);
  }

  search(id: number, emplid: string, page = 0, size = 1000): Observable<Page<ThirdPartyLicenseManagement>> {
    return this.dataService.searchTplm(id, emplid, page, size);
  }

  isLatestActive(id: number, emplid: string): Observable<boolean> {
    return this.search(id, emplid)
      .pipe(
        map(page =>
          page.data.slice().sort((a, b) => {
            const diff = b.effectiveDate.localeCompare(a.effectiveDate);
            if (diff === 0) {
              return b.effectiveSequence - a.effectiveSequence;
            }
            return diff;
          })),
        map(data => !!data.length && data[0].action === 'ACTIVATE'));
  }

  fetchUntilSynchronizedOrError(id: number): Observable<ThirdPartyLicenseManagement> {
    return this.fetchTplm(id).pipe(
      switchMap((m: ThirdPartyLicenseManagement) =>
        (m.status === ThirdPartyLicenseManagementStatus.Created)
          ? throwError(() => 'Management has not been synchronized with vendor')
          : of(m),
      ),
      retry({
        // retryCount starts at 1
        delay: (_, retryCount) => {
          // divide retryCount by 2 to only increase the fibonacci number on every other retry
          const sequence = Math.floor(retryCount / 2);
          // divide the fibonacci number by 2 to retry more quickly
          const duration = this.fibonacci(sequence) / 2;
          // multiply by 1000 to get milliseconds
          return timer(duration * 1000);
        },
      }),
    );
  }

  fibonacci(n: number): number {
    let fibN = 0;
    let fibNPlus1 = 1;
    for (let i = 0; i < n; i++) {
      const fibNPlus2 = fibN + fibNPlus1;
      fibN = fibNPlus1;
      fibNPlus1 = fibNPlus2;
    }
    return fibN;
  }
}
